import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Instagram, Linkedin, Globe, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { contactData } from '../mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    projectLocation: '',
    hearAboutUs: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send to backend API
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: "Message Sent Successfully!",
          description: "We'll get back to you within 24 hours.",
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          projectLocation: '',
          hearAboutUs: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "There was an error submitting your form. Please try again or contact us directly.",
        variant: "destructive"
      });
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactData.whatsapp}?text=Hello! I'm interested in your interior design services.`, '_blank');
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-[#F5F5F5] relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF6B00] opacity-5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#FF6B00] opacity-5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            Let's Create <span className="text-[#FF6B00]">Together</span>
          </h2>
          <div className="w-24 h-1 bg-[#FF6B00] mx-auto mb-6"></div>
          <p className="text-[#333333] text-lg max-w-2xl mx-auto">
            Ready to transform your space? Get in touch with our expert team today
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Information - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Cards */}
            <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start">
                <div className="bg-[#FF6B00] bg-opacity-10 p-4 rounded-xl mr-4">
                  <MapPin className="text-[#FF6B00]" size={28} />
                </div>
                <div>
                  <p className="font-bold text-black text-lg mb-2">Visit Us</p>
                  <p className="text-[#333333] text-sm leading-relaxed">{contactData.address}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start">
                <div className="bg-[#FF6B00] bg-opacity-10 p-4 rounded-xl mr-4">
                  <Phone className="text-[#FF6B00]" size={28} />
                </div>
                <div>
                  <p className="font-bold text-black text-lg mb-2">Call Us</p>
                  <a href={`tel:${contactData.phone}`} className="text-[#333333] hover:text-[#FF6B00] transition-colors block text-sm">
                    {contactData.phone}
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start">
                <div className="bg-[#FF6B00] bg-opacity-10 p-4 rounded-xl mr-4">
                  <Mail className="text-[#FF6B00]" size={28} />
                </div>
                <div>
                  <p className="font-bold text-black text-lg mb-2">Email Us</p>
                  <a href={`mailto:${contactData.email}`} className="text-[#333333] hover:text-[#FF6B00] transition-colors block text-sm mb-1">
                    {contactData.email}
                  </a>
                  <a href={`mailto:${contactData.email2}`} className="text-[#333333] hover:text-[#FF6B00] transition-colors block text-sm">
                    {contactData.email2}
                  </a>
                </div>
              </div>
            </div>

            {/* Social Media Icons */}
            <div className="bg-gradient-to-br from-[#FF6B00] to-[#E65F00] p-6 rounded-2xl shadow-lg">
              <p className="font-bold text-white text-lg mb-4">Connect With Us</p>
              <div className="flex gap-4">
                <button
                  onClick={handleWhatsApp}
                  className="bg-white/20 backdrop-blur-sm hover:bg-white/30 p-3 rounded-xl transition-all duration-300 hover:scale-110 group"
                  title="WhatsApp"
                >
                  <MessageCircle className="text-white" size={24} />
                </button>
                <button
                  onClick={() => window.open(contactData.instagram, '_blank')}
                  className="bg-white/20 backdrop-blur-sm hover:bg-white/30 p-3 rounded-xl transition-all duration-300 hover:scale-110 group"
                  title="Instagram"
                >
                  <Instagram className="text-white" size={24} />
                </button>
                <button
                  onClick={() => window.open(contactData.linkedin, '_blank')}
                  className="bg-white/20 backdrop-blur-sm hover:bg-white/30 p-3 rounded-xl transition-all duration-300 hover:scale-110 group"
                  title="LinkedIn"
                >
                  <Linkedin className="text-white" size={24} />
                </button>
                <button
                  onClick={() => window.open(`https://${contactData.website}`, '_blank')}
                  className="bg-white/20 backdrop-blur-sm hover:bg-white/30 p-3 rounded-xl transition-all duration-300 hover:scale-110 group"
                  title="Website"
                >
                  <Globe className="text-white" size={24} />
                </button>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden shadow-lg h-64">
              <iframe
                src={contactData.mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Lalit Interior Location"
              ></iframe>
            </div>
          </div>

          {/* Contact Form - 3 columns */}
          <div className="lg:col-span-3">
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl">
              <h3 className="text-3xl font-bold text-black mb-2">Send Us a Message</h3>
              <p className="text-[#333333] mb-8">Fill out the form below and we'll get back to you shortly</p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-2">Your Name *</label>
                    <Input
                      type="text"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-[#F5F5F5] border-0 h-12 focus:ring-2 focus:ring-[#FF6B00] rounded-xl"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="bg-[#F5F5F5] border-0 h-12 focus:ring-2 focus:ring-[#FF6B00] rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#333333] mb-2">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-[#F5F5F5] border-0 h-12 focus:ring-2 focus:ring-[#FF6B00] rounded-xl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#333333] mb-2">Project Type *</label>
                  <select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#F5F5F5] border-0 h-12 rounded-xl px-4 text-[#333333] focus:ring-2 focus:ring-[#FF6B00] focus:outline-none"
                  >
                    <option value="">Select your project type</option>
                    <option value="residential">Residential Interior</option>
                    <option value="commercial">Commercial Space</option>
                    <option value="hospitality">Hotel & Hospitality</option>
                    <option value="institutional">Institutional Project</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#333333] mb-2">Project Location *</label>
                  <Input
                    type="text"
                    name="projectLocation"
                    placeholder="City, State"
                    value={formData.projectLocation}
                    onChange={handleChange}
                    required
                    className="bg-[#F5F5F5] border-0 h-12 focus:ring-2 focus:ring-[#FF6B00] rounded-xl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#333333] mb-2">How did you hear about us? *</label>
                  <select
                    name="hearAboutUs"
                    value={formData.hearAboutUs}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#F5F5F5] border-0 h-12 rounded-xl px-4 text-[#333333] focus:ring-2 focus:ring-[#FF6B00] focus:outline-none"
                  >
                    <option value="">Please select</option>
                    <option value="google">Google Search</option>
                    <option value="instagram">Instagram</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="facebook">Facebook</option>
                    <option value="referral">Referral from Friend/Family</option>
                    <option value="website">Company Website</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#333333] mb-2">Project Details *</label>
                  <Textarea
                    name="message"
                    placeholder="Tell us about your project requirements..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-[#F5F5F5] border-0 focus:ring-2 focus:ring-[#FF6B00] rounded-xl"
                  />
                </div>

                <Button
                  type="submit"
                  className="bg-gradient-to-r from-[#FF6B00] to-[#E65F00] hover:from-[#E65F00] hover:to-[#FF6B00] text-white w-full py-6 text-lg font-semibold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  <Send className="mr-2" size={20} />
                  Send Message
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Corporate Footer */}
      <div className="mt-20 bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="container mx-auto px-6 py-12">
          {/* Top Footer - Corporate Info */}
          <div className="grid md:grid-cols-3 gap-12 mb-12 pb-12 border-b border-gray-800">
            {/* Company Info with White Logo */}
            <div>
              <img 
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_lalit-precision/artifacts/f8086f8de46bb5fa_2.png" 
                alt="Lalit Interior Logo" 
                className="h-16 mb-6 brightness-0 invert" 
              />
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                India's premier turnkey interior execution partner, delivering excellence in hospitality, commercial, and residential projects since 2016.
              </p>
              <div className="flex gap-4 mt-6">
                <a 
                  href={contactData.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#FF6B00] transition-colors p-2 bg-white/5 rounded-lg hover:bg-white/10"
                >
                  <Instagram size={20} />
                </a>
                <a 
                  href={contactData.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#FF6B00] transition-colors p-2 bg-white/5 rounded-lg hover:bg-white/10"
                >
                  <Linkedin size={20} />
                </a>
                <a 
                  href={`https://wa.me/${contactData.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#FF6B00] transition-colors p-2 bg-white/5 rounded-lg hover:bg-white/10"
                >
                  <MessageCircle size={20} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-bold text-lg mb-6">Services</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="hover:text-[#FF6B00] transition-colors cursor-pointer">Turnkey Interior Execution</li>
                <li className="hover:text-[#FF6B00] transition-colors cursor-pointer">MEP & Fire Safety Systems</li>
                <li className="hover:text-[#FF6B00] transition-colors cursor-pointer">Commercial Projects</li>
                <li className="hover:text-[#FF6B00] transition-colors cursor-pointer">Hospitality Interiors</li>
                <li className="hover:text-[#FF6B00] transition-colors cursor-pointer">Residential Solutions</li>
              </ul>
            </div>

            {/* Corporate Info */}
            <div>
              <h4 className="text-white font-bold text-lg mb-6">Corporate Office</h4>
              <div className="space-y-4 text-gray-400 text-sm">
                <p className="leading-relaxed">
                  {contactData.address}
                </p>
                <div className="space-y-2">
                  <p className="flex items-center gap-2">
                    <Phone size={16} className="text-[#FF6B00]" />
                    <a href={`tel:${contactData.phone}`} className="hover:text-[#FF6B00] transition-colors">
                      {contactData.phone}
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail size={16} className="text-[#FF6B00]" />
                    <a href={`mailto:${contactData.email}`} className="hover:text-[#FF6B00] transition-colors">
                      {contactData.email}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Footer - Copyright */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-gray-500 text-sm">
            <div className="text-center md:text-left">
              <p className="font-semibold text-white mb-2">© 2024 Lalit Interior Pvt. Ltd. All Rights Reserved.</p>
              <p className="text-xs">
                Excellence in Turnkey Interior Execution | PAN-India Operations
              </p>
            </div>
            
            <div className="flex gap-6 text-xs">
              <button className="hover:text-[#FF6B00] transition-colors">Privacy Policy</button>
              <span>|</span>
              <button className="hover:text-[#FF6B00] transition-colors">Terms & Conditions</button>
              <span>|</span>
              <button 
                onClick={() => {
                  const element = document.getElementById('careers');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="hover:text-[#FF6B00] transition-colors cursor-pointer"
              >
                Careers
              </button>
            </div>
          </div>

          {/* Corporate Note */}
          <div className="mt-8 pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-500 text-xs leading-relaxed max-w-4xl mx-auto">
              <span className="text-gray-400 font-semibold">Lalit Interior</span> is a professionally managed organization specializing in comprehensive turnkey interior solutions. 
              With over 8 years of industry expertise, we deliver world-class projects across hospitality, commercial, and residential sectors. 
              Our commitment to quality, transparency, and client satisfaction has made us a trusted partner for leading brands across India.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
